import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Search, ShoppingCart, Package, Plus, Minus, Loader2, Building2, Truck, History, IndianRupee } from 'lucide-react';
import AddressPickerCheckout from './AddressPickerCheckout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChemistMarketplace({ token: propToken }) {
  const getHeaders = () => ({ Authorization: `Bearer ${propToken || localStorage.getItem('vendor_token')}` });

  const [tab, setTab] = useState('browse');
  const [linkedPharma, setLinkedPharma] = useState([]);
  const [selectedPharma, setSelectedPharma] = useState('all');
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [gstPct, setGstPct] = useState(18);
  const [applyGst, setApplyGst] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  const fetchLinkedPharma = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-vendor-mappings/my-connections`, { headers: getHeaders() });
      setLinkedPharma(res.data.filter(c => c.status === 'approved'));
    } catch (err) { console.error(err); }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedPharma !== 'all') params.append('vendor_id', selectedPharma);
      const res = await axios.get(`${API}/api/chemist/pharma-products?${params}`, { headers: getHeaders() });
      setProducts(res.data);
    } catch { toast.error('Failed to load products'); }
    setLoading(false);
  }, [search, selectedPharma]);

  const fetchMyOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/api/b2b/orders/my-orders`, { headers: getHeaders() });
      setMyOrders(Array.isArray(res.data) ? res.data : res.data.orders || []);
    } catch (err) { console.error(err); }
    setOrdersLoading(false);
  }, []);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vendor-wallet/balance`, { headers: getHeaders() });
      setWalletBalance(res.data.balance || 0);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchLinkedPharma(); fetchWallet(); }, [fetchLinkedPharma, fetchWallet]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateCartQty = (productId, delta) => {
    setCart(prev => prev.map(i => {
      if (i.id !== productId) return i;
      const newQty = Math.max(0, i.quantity + delta);
      return newQty === 0 ? null : { ...i, quantity: newQty };
    }).filter(Boolean));
  };

  const cartTotal = cart.reduce((s, i) => s + (i.b2b_price || i.mrp || 0) * i.quantity, 0);

  const placeOrder = async () => {
    if (cart.length === 0) return;
    setPlacing(true);
    try {
      // Group cart by vendor
      const vendorGroups = {};
      cart.forEach(item => {
        const vid = item.vendor_id;
        if (!vendorGroups[vid]) vendorGroups[vid] = [];
        vendorGroups[vid].push({ item_id: item.id, item_type: 'medicine', item_name: item.name, quantity: item.quantity, unit_price: item.b2b_price || item.mrp || 0, total_price: (item.b2b_price || item.mrp || 0) * item.quantity });
      });

      for (const [vendorId, items] of Object.entries(vendorGroups)) {
        await axios.post(`${API}/api/b2b/orders`, {
          vendor_id: vendorId,
          items,
          notes: orderNotes,
          delivery_address: deliveryAddress,
          delivery_phone: deliveryPhone || '',
          delivery_location: deliveryLocation || null,
          gst_percent: gstPct,
          apply_gst: applyGst
        }, { headers: getHeaders() });
      }

      toast.success(`Order${Object.keys(vendorGroups).length > 1 ? 's' : ''} placed successfully!`);
      setCart([]);
      setShowCheckout(false);
      setOrderNotes('');
      fetchWallet();
      fetchMyOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    }
    setPlacing(false);
  };

  return (
    <div className="space-y-4" data-testid="chemist-marketplace">
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'my-orders') fetchMyOrders(); }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList>
            <TabsTrigger value="browse" data-testid="browse-products-tab"><Package className="w-4 h-4 mr-1" /> Browse Products</TabsTrigger>
            <TabsTrigger value="my-orders" data-testid="my-orders-tab"><History className="w-4 h-4 mr-1" /> My Orders</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-3">
            <div className="bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-lg text-sm">
              <span className="text-emerald-600 font-medium">Wallet: </span>
              <span className="font-bold text-emerald-700">₹{walletBalance.toLocaleString('en-IN')}</span>
            </div>
            {cart.length > 0 && (
              <Button onClick={() => setShowCheckout(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="checkout-btn">
                <ShoppingCart className="w-4 h-4 mr-1" /> Cart ({cart.length}) - ₹{cartTotal.toFixed(0)}
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="browse">
          <div className="flex flex-wrap gap-2 mb-4">
            <Input placeholder="Search medicines..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-56 h-9" data-testid="pharma-product-search" />
            <Select value={selectedPharma} onValueChange={setSelectedPharma}>
              <SelectTrigger className="w-48 h-9"><SelectValue placeholder="All Pharma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Linked Pharma</SelectItem>
                {linkedPharma.map(p => <SelectItem key={p.linked_vendor_id || p.vendor_id} value={p.linked_vendor_id || p.vendor_id}>{p.linked_vendor_name || p.vendor_name || 'Pharma'}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchProducts}><Search className="w-4 h-4" /></Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No products found. Link with pharma companies to see their products.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {products.map(product => {
                const inCart = cart.find(i => i.id === product.id);
                return (
                  <Card key={product.id} className="hover:shadow-md transition" data-testid={`product-card-${product.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{product.name}</p>
                          <p className="text-xs text-slate-500 truncate">{product.generic_name || product.manufacturer || ''}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{product.vendor_name}</p>
                        </div>
                        {product.photo_url && <img src={`${API}${product.photo_url}`} alt="" className="w-10 h-10 rounded object-cover ml-2" />}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-emerald-600">₹{product.b2b_price || product.mrp}</p>
                          {product.b2b_price && product.mrp && product.b2b_price < product.mrp && (
                            <p className="text-[10px] text-slate-400 line-through">MRP ₹{product.mrp}</p>
                          )}
                          {product.stock_quantity != null && <p className="text-[10px] text-slate-400">Stock: {product.stock_quantity}</p>}
                        </div>
                        {inCart ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateCartQty(product.id, -1)}><Minus className="w-3 h-3" /></Button>
                            <span className="text-sm font-medium w-6 text-center">{inCart.quantity}</span>
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateCartQty(product.id, 1)}><Plus className="w-3 h-3" /></Button>
                          </div>
                        ) : (
                          <Button size="sm" onClick={() => addToCart(product)} data-testid={`add-to-cart-${product.id}`}>
                            <Plus className="w-3 h-3 mr-1" /> Add
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="my-orders">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">My B2B Orders</CardTitle>
              <CardDescription>Orders you've placed with pharma companies</CardDescription>
            </CardHeader>
            <CardContent>
              {ordersLoading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div> :
              myOrders.length === 0 ? <p className="text-center text-slate-400 py-8">No orders placed yet</p> : (
                <div className="space-y-3">
                  {myOrders.map(order => (
                    <div key={order.id} className="border rounded-lg p-3" data-testid={`order-${order.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-xs font-mono text-slate-500">{order.order_number}</p>
                          <p className="font-medium text-sm">{order.vendor_name || 'Pharma Vendor'}</p>
                          <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">₹{order.total?.toLocaleString()}</p>
                          <Badge variant={order.status === 'delivered' ? 'default' : order.status === 'pending' ? 'secondary' : 'outline'} className="text-xs capitalize">{order.status}</Badge>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded p-2 text-xs">
                        {order.items?.map((item, i) => (
                          <div key={`el-${i}`} className="flex justify-between">{item.item_name} x{item.quantity} <span>₹{item.total_price}</span></div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Checkout</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="max-h-48 overflow-y-auto space-y-2">
              {cart.map(item => (
                <div key={item.id} className="flex items-center justify-between text-sm border-b pb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{item.name}</p>
                    <p className="text-xs text-slate-400">{item.vendor_name}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => updateCartQty(item.id, -1)}><Minus className="w-3 h-3" /></Button>
                    <span className="w-5 text-center text-xs">{item.quantity}</span>
                    <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => updateCartQty(item.id, 1)}><Plus className="w-3 h-3" /></Button>
                    <p className="font-medium w-16 text-right">₹{((item.b2b_price || item.mrp) * item.quantity).toFixed(0)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600">Delivery Address & Phone</label>
              <AddressPickerCheckout
                token={propToken || localStorage.getItem('vendor_token')}
                initialAddress={deliveryAddress}
                onAddressSelected={(data) => {
                  setDeliveryAddress(data.address || '');
                  setDeliveryPhone(data.phone || '');
                  setDeliveryLocation(data.location || null);
                }}
              />
            </div>
            <Textarea placeholder="Order notes (optional)" value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} rows={2} />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={applyGst} onChange={(e) => setApplyGst(e.target.checked)} /> Apply GST
              </label>
              {applyGst && <Input type="number" min="0" max="28" value={gstPct} onChange={(e) => setGstPct(parseFloat(e.target.value) || 0)} className="w-20 h-7 text-xs" placeholder="GST %" />}
            </div>
            <div className="border-t pt-3 space-y-1">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>₹{cartTotal.toFixed(2)}</span></div>
              {applyGst && <div className="flex justify-between text-sm"><span>GST ({gstPct}%)</span><span>₹{(cartTotal * gstPct / 100).toFixed(2)}</span></div>}
              <div className="flex justify-between text-sm font-bold"><span>Total</span><span>₹{(cartTotal + (applyGst ? cartTotal * gstPct / 100 : 0)).toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span>Wallet Balance</span><span className={walletBalance >= (cartTotal + (applyGst ? cartTotal * gstPct / 100 : 0)) ? 'text-emerald-600' : 'text-red-500'}>₹{walletBalance.toLocaleString()}</span></div>
              {walletBalance < (cartTotal + (applyGst ? cartTotal * gstPct / 100 : 0)) && <p className="text-xs text-red-500">Insufficient wallet balance. Please top up.</p>}
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={placeOrder} disabled={placing || !deliveryAddress || walletBalance < (cartTotal + (applyGst ? cartTotal * gstPct / 100 : 0))} data-testid="place-order-btn">
              {placing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
              Place Order (₹{cartTotal.toFixed(0)})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
